import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const FortStJohn = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.fort-st-john:About Fort St John Paragraph 1">
          Fort St. John, located in northeastern{' '}
          <LinkText href="/rental-listings/british-columbia">
            British Columbia (BC)
          </LinkText>
          , Canada, is a thriving city known for its economic significance,
          cultural diversity, and natural beauty. As the largest city in the
          Peace River Regional District, it stands as a key economic hub, driven
          by industries such as oil and gas, agriculture, and forestry. This
          city is a melting pot of cultures, hosting various events and
          festivals. Residents and visitors alike can enjoy abundant outdoor
          activities like hiking, fishing, and snowmobiling, thanks to its
          proximity to the Peace River and wilderness areas. Fort St. John is
          equipped with educational institutions, healthcare facilities, and
          robust transportation connections via the Alaska Highway. Its growth
          continues with ongoing economic development initiatives, making it a
          notable city in the landscape of{' '}
          <LinkText href={BcNeighbourhoodsLink}>
            cities and neighbourhoods in BC
          </LinkText>
          .
        </Trans>
      </Paragraph>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$800 - $1,800',
          bed2: '$1,000 - $2,200',
          bed3: '$1,300 - $2,800'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.fort-st-john:Apartment Hunting in Fort St John Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Fort St. John
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Apartments for rent Fort St John Paragraph">
              <LinkText href={links.apartment}>
                Renting an apartment in Fort St. John
              </LinkText>{' '}
              is popular among singles, couples, and small families. Apartments
              for rent in Fort St. John range from older, more budget-friendly
              options to newer, luxury units. Many are conveniently located near
              amenities such as shopping centers, restaurants, and parks, making
              them ideal for those seeking a balance of comfort and convenience.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Houses for rent Fort St John Paragraph">
              <LinkText href={links.house}>
                Houses for rent in Fort St. John
              </LinkText>{' '}
              are a preferred choice for families or individuals looking for
              more space and privacy. These properties often include yards,
              providing room for outdoor activities, which is particularly
              appealing in this region known for its outdoor lifestyle. The
              variety of houses ranges from older, character-filled homes to
              newer constructions, accommodating different preferences and
              budgets.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Townhouses for rent Fort St John Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in Fort St. John
              </LinkText>{' '}
              offer a compromise between apartment and house living. These units
              are popular among small families, professionals, or couples.
              Townhouses typically provide multiple floors of living space and
              may include amenities like small yards or patios, offering a sense
              of community with more privacy than a standard apartment.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Basements for rent Fort St John Paragraph">
              Basements for rent in Fort St. John offer a more affordable rental
              option. These units, generally part of larger homes, are a popular
              choice for students, singles, or those on a tighter budget. While
              they may have limited natural light, basement suites provide a
              private living space at a lower cost.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Condos for rent Fort St John Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in Fort St. John
              </LinkText>{' '}
              cater to those seeking a low-maintenance lifestyle. These units
              often include modern amenities and are designed for ease of
              living. Condos are a favorite among young professionals, small
              families, or retirees who value the convenience of having
              facilities like fitness centers or communal areas.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Rooms for rent Fort St John Paragraph">
              <LinkText href={links.room}>
                Rooms for rent in Fort St. John
              </LinkText>{' '}
              are the most budget-friendly housing option, commonly chosen by
              students, temporary workers, or individuals seeking flexible
              living arrangements. This option usually involves sharing common
              areas like kitchens and bathrooms with other occupants, offering a
              communal and social living experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Short term rentals Fort St John Paragraph">
              Fort St. John, known primarily for its strong energy sector,
              particularly in oil and gas, provides numerous employment
              opportunities related to these industries. While it has
              educational facilities, it may not be particularly noted for a
              wide range of academic institutions. The city does see some demand
              for <LinkText href={links.shortTerm}>short-term rentals</LinkText>
              , often driven by the transient nature of work in the energy
              sector rather than the conventional factors that make short-term
              rentals popular in larger cities or university towns. In Fort St.
              John, this demand can be somewhat consistent year-round, rather
              than peaking during summer months and the beginning of school
              semesters as seen in more urban areas. For many, the{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                flexibility offered by short-term rentals is an important
                consideration
              </LinkText>
              , especially for those whose work or personal circumstances
              require temporary or adaptable living arrangements.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.fort-st-john:Cheap rentals Fort St John Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Fort St. John
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Fort St. John, there are neighbourhoods
              where rent is considerably cheaper, so exploring these options is
              a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.fort-st-john:Living in Fort St John Paragraph 1">
            Living in Fort St. John, British Columbia, offers a unique
            experience of northern beauty and small-town charm. Situated in the
            Peace River region, Fort St. John boasts stunning views of the
            surrounding mountains, lush forests, and pristine rivers. The
            close-knit community is renowned for its warm and friendly
            atmosphere, ensuring a plethora of amenities for both locals and
            visitors. Adventurers can indulge in outdoor activities like hiking,
            fishing, and skiing, while culture enthusiasts can immerse
            themselves in the local art and history scene. With its tranquil and
            laid-back ambiance, Fort St. John provides an ideal setting for a
            peaceful mountain lifestyle.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.fort-st-john:Living in Fort St John Paragraph 2">
            If you’re not sure that Fort St. John is the right city for you,
            check out some of these resources comparing the different cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.fort-st-john:Popular Neighbourhoods Fort St John Paragraph">
          Like any city, Fort St. John is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Downtown Fort St. John, North Peace, Southridge, Alaska
          Highway, East Bypass, and West Bypass. For more info about Fort St.
          John, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.fort-st-john:Local Landmarks Fort St John Paragraph">
          Fort St. John, BC, is a city filled with remarkable landmarks that
          showcase the area’s natural beauty and cultural significance. One of
          the prominent landmarks in Fort St. John is the Northern Lights
          College, a modern educational institution that caters to the
          community’s learning needs. Another notable landmark is the Peace
          River, which flows through the city and provides breathtaking views
          and recreational opportunities like fishing and boating. For history
          enthusiasts, the Fort St. John North Peace Museum offers a glimpse
          into the region’s past, with exhibits showcasing the area’s Indigenous
          heritage and pioneer history. Finally, the Beatton Provincial Park is
          a stunning natural landmark that features picturesque landscapes,
          hiking trails, and camping facilities. These landmarks contribute to
          the allure of Fort St. John, making it a captivating destination for
          locals and tourists alike.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default FortStJohn
